import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  keyframes
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

function UserPricing() {
  const pulseGlow = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(79, 70, 229, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
    }
  `;

  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState({});
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);

  React.useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setFetchingPlans(true);
      const paymentBasePath = (api.defaults.baseURL || '').endsWith('/api')
        ? '/payment'
        : '/api/payment';
      
      const response = await api.get(`${paymentBasePath}/plans`);
      const backendPlans = response.data.plans || [];
      
      // Filter for candidate plans only
      const candidatePlans = backendPlans.filter(p => p.code.startsWith('CANDIDATE_'));

      // Transform backend plans to UI format
      const mappedPlans = candidatePlans.map(p => {
        let period = '';
        if (p.code === 'CANDIDATE_MONTHLY') period = '/month';
        else if (p.code === 'CANDIDATE_ANNUAL') period = '/year';

        return {
          title: p.name,
          price: `₹${(p.amount / 100).toLocaleString()}`,
          period: period,
          planCode: p.code.toLowerCase(),
          description: p.description,
          features: p.features || [],
          buttonText: 'Subscribe',
          variant: p.code === 'CANDIDATE_ANNUAL' ? 'contained' : 'outlined',
          popular: p.code === 'CANDIDATE_ANNUAL'
        };
      });

      setPlans(mappedPlans);
    } catch (err) {
      console.error('Failed to fetch candidate plans:', err);
      setError('Failed to load pricing plans. Please try again later.');
    } finally {
      setFetchingPlans(false);
    }
  };

  async function handleSubscribe(planCode) {
    if (!isAuthenticated) {
      alert('Please login first to subscribe.');
      navigate('/login');
      return;
    }

    setLoading(prev => ({ ...prev, [planCode]: true }));
    setError('');

    try {
      // Support both local Express (baseURL ends with /api, routes under /payment)
      // and production API Gateway/Lambda (routes under /api/payment)
      const paymentBasePath = (api.defaults.baseURL || '').endsWith('/api')
        ? '/payment'
        : '/api/payment';

      const orderRes = await api.post(`${paymentBasePath}/create-order`, {
        planCode: planCode.toLowerCase(),
      });
      const { orderId, amount, currency, key } = orderRes.data;

      if (!orderId || !key) {
        throw new Error('Invalid response from payment server. Please try again later.');
      }

      const options = {
        key,
        amount,
        currency,
        name: 'ExiHire',
        description: `${planCode.charAt(0).toUpperCase() + planCode.slice(1)} Candidate Plan`,
        order_id: orderId,
        handler: async function (response) {
          try {
            const verifyRes = await api.post(`${paymentBasePath}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planCode: planCode.toLowerCase()
            });

            if (verifyRes.data.success) {
              alert('Plan activated successfully!');
              navigate('/jobs'); // Redirect to jobs page
            }
          } catch (err) {
            console.error('Payment verification failed:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: `${user?.firstName || ''} ${user?.lastName || ''}`,
          email: user?.email || '',
        },
        theme: { color: '#4F46E5' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Failed to initiate subscription:', err);

      // Better message for CORS / API Gateway mapping failures (no response)
      if (!err.response) {
        setError('Unable to connect to the payment service. Please try again in a moment.');
      } else {
        setError(err.response?.data?.message || 'Failed to initiate plan. Please try again.');
      }
    } finally {
      setLoading(prev => ({ ...prev, [planCode]: false }));
    }
  }

  return (
    <Container sx={{ py: 8 }}>
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
      <Typography variant="h3" align="center" sx={{ mb: 6, color: '#4338CA', fontWeight: 'bold' }}>
        Choose Your Plan
      </Typography>
      {fetchingPlans ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={4} alignItems="stretch">
          {plans.map((plan, index) => (
            <Grid item key={index} xs={12} md={4} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: plan.popular ? '2px solid #4F46E5' : 'none',
                  boxShadow: plan.popular ? 6 : 1,
                  transform: plan.popular ? 'scale(1.05)' : 'none',
                  zIndex: plan.popular ? 1 : 0,
                  width: '100%'
                }}
              >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 9,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: '#4F46E5',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}
                >
                  Most Popular
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" component="h2" align="center" gutterBottom fontWeight="bold">
                  {plan.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="h3" component="span" fontWeight="bold">{plan.price}</Typography>
                  <Typography variant="subtitle1" component="span" sx={{ ml: 1, color: 'text.secondary' }}>{plan.period}</Typography>
                </Box>
                {plan.planCode === 'candidate_annual' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mb: 3,
                      mt: -1,
                      py: '10px',
                      px: '20px',
                      borderRadius: '999px',
                      background: 'linear-gradient(90deg, #4F46E5, #60A5FA)',
                      color: 'white',
                      boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
                      width: 'fit-content',
                      maxWidth: '90%',
                      mx: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      🚀 Launch Offer — Exclusive Pricing for First 100 Customers
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
                  {plan.description}
                </Typography>
                <List sx={{ mb: 4 }}>
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: '#4F46E5', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <Box sx={{ p: 3, mt: 'auto' }}>
                <Button
                  fullWidth
                  variant={plan.variant}
                  size="large"
                  color="primary"
                  disabled={loading[plan.planCode]}
                  onClick={() => handleSubscribe(plan.planCode)}
                  sx={{ py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
                >
                  {loading[plan.planCode] ? <CircularProgress size={24} /> : plan.buttonText}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}
    </Container>
  );
}

export default UserPricing;
